import { classNames } from "@/utils/classNames";
import { orderlessFilter, tokenizeFilterString } from "@/utils/filter";
import { mergeRefs, useElementId } from "@/utils/reactHelpers";
import { Autocomplete as BaseAutocomplete } from "@base-ui/react/autocomplete";
import { MutableRefObject, ReactNode, Ref, useEffect, useMemo, useRef, useState } from "react";
import { TextInput, TextInputProps } from "./TextInput";
import { useVirtualizer } from "@tanstack/react-virtual";

export type Autocomplete2InputProps = Omit<
  TextInputProps,
  | "id"
  | "value"
  | "type"
  | "inputRef"
  | "inputProps"
  | "onChange"
  | "onFocus"
  | "onBlur"
  | "onKeyDown"
  | "onPaste"
>;

export type Autocomplete2ItemState = {
  disabled: boolean;
  highlighted: boolean;
};

export type Autocomplete2SearchTextFn<T> = (item: T) => string | undefined | (string | undefined)[];

export type Autocomplete2RenderItemFn<T> = (
  item: T,
  queryTokens: string[],
  state: Autocomplete2ItemState,
) => ReactNode;

export type Autocomplete2Props<T> = {
  id?: string;
  items: T[];
  itemTextFn: (item: T) => string;
  itemSearchTextFn?: Autocomplete2SearchTextFn<T>;
  renderItemFn: Autocomplete2RenderItemFn<T>;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string, eventDetails: BaseAutocomplete.Root.ChangeEventDetails) => void;
  open?: boolean;
  onOpenChange?: (open: boolean, eventDetails: BaseAutocomplete.Root.ChangeEventDetails) => void;
  onSelect?: (item: T) => void;
  inputProps?: Autocomplete2InputProps;
  inputRef?: Ref<HTMLInputElement>;
  openOnInputClick?: boolean;
  inline?: boolean;
  header?: ReactNode;
  emptyMessage?: ReactNode;
};

const ESTIMATED_ITEM_HEIGHT = 48;
const OVERSCAN_ITEMS = 10;

type ScrollToIndexRef = MutableRefObject<((index: number) => void) | undefined>;

type VirtualizedListProps<T> = {
  items: T[];
  queryTokens: string[];
  renderItemFn: Autocomplete2RenderItemFn<T>;
  onSelect?: (item: T) => void;
  scrollToIndexRef: ScrollToIndexRef;
};

function VirtualizedList<T>({
  items,
  queryTokens,
  renderItemFn,
  onSelect,
  scrollToIndexRef,
}: VirtualizedListProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT,
    overscan: OVERSCAN_ITEMS,
  });
  useEffect(() => {
    scrollToIndexRef.current = (index) => virtualizer.scrollToIndex(index);
    return () => {
      scrollToIndexRef.current = undefined;
    };
  }, [scrollToIndexRef, virtualizer]);
  return (
    <BaseAutocomplete.List ref={listRef} className="grow overflow-auto">
      <div
        role="presentation"
        style={{ height: virtualizer.getTotalSize(), width: "100%", position: "relative" }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = items[virtualItem.index];
          return (
            <BaseAutocomplete.Item
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              index={virtualItem.index}
              value={item}
              data-index={virtualItem.index}
              aria-setsize={items.length}
              aria-posinset={virtualItem.index + 1}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
              }}
              onClick={() => onSelect?.(item)}
              render={(itemProps, state) => (
                <div {...itemProps}>{renderItemFn(item, queryTokens, state)}</div>
              )}
            />
          );
        })}
      </div>
    </BaseAutocomplete.List>
  );
}

export function Autocomplete2<T>({
  items,
  itemTextFn,
  itemSearchTextFn,
  renderItemFn,
  value,
  defaultValue,
  onValueChange,
  open,
  onOpenChange,
  onSelect,
  inputProps,
  inputRef,
  openOnInputClick,
  inline,
  header,
  emptyMessage,
  ...props
}: Autocomplete2Props<T>) {
  const id = useElementId(props.id);
  const anchorRef = useRef<HTMLDivElement>(null);
  const scrollToIndexRef: ScrollToIndexRef = useRef(undefined);
  const [internalQuery, setInternalQuery] = useState(defaultValue ?? "");
  const query = value ?? internalQuery;
  const searchTextFn = itemSearchTextFn ?? itemTextFn;
  const queryTokens = useMemo(() => tokenizeFilterString(query), [query]);
  const filteredItems = useMemo(
    () => orderlessFilter(items, searchTextFn, query),
    [items, searchTextFn, query],
  );
  const hideWhenEmpty = header === undefined && emptyMessage === undefined;
  const listContent = (
    <>
      {header !== undefined && (
        <div className={classNames("z-10", "bg-default", "border-b")}>{header}</div>
      )}
      {emptyMessage !== undefined && (
        <BaseAutocomplete.Empty>
          <div className={classNames("px-4", "py-3", "text-sm", "subtle")}>{emptyMessage}</div>
        </BaseAutocomplete.Empty>
      )}
      <VirtualizedList
        items={filteredItems}
        queryTokens={queryTokens}
        renderItemFn={renderItemFn}
        onSelect={onSelect}
        scrollToIndexRef={scrollToIndexRef}
      />
    </>
  );
  return (
    <BaseAutocomplete.Root
      id={id}
      items={items}
      filteredItems={filteredItems}
      itemToStringValue={itemTextFn}
      virtualized
      onItemHighlighted={(_, eventDetails) => {
        if (eventDetails.reason === "keyboard" && eventDetails.index !== -1) {
          scrollToIndexRef.current?.(eventDetails.index);
        }
      }}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(newValue, eventDetails) => {
        setInternalQuery(newValue);
        onValueChange?.(newValue, eventDetails);
      }}
      open={inline || open}
      onOpenChange={onOpenChange}
      disabled={inputProps?.disabled}
      readOnly={inputProps?.readOnly}
      name={inputProps?.name}
      openOnInputClick={openOnInputClick}
      inline={inline}
      autoHighlight={"always"}
    >
      <BaseAutocomplete.Input
        render={({ ref, ...baseInputProps }) => (
          <TextInput
            {...inputProps}
            ref={anchorRef}
            id={id}
            inputRef={mergeRefs(ref, inputRef)}
            inputProps={baseInputProps}
          />
        )}
      />
      {inline ? (
        <div className={classNames("flex", "flex-col", "grow", "min-h-0")}>{listContent}</div>
      ) : (
        <BaseAutocomplete.Portal>
          <BaseAutocomplete.Positioner anchor={anchorRef} align={"start"} sideOffset={4}>
            <BaseAutocomplete.Popup
              style={{
                transformOrigin: "var(--transform-origin)",
                maxHeight: "var(--available-height, 50vh)",
                minWidth: "var(--anchor-width)",
                maxWidth: "var(--anchor-width)",
              }}
              className={classNames(
                "rounded-sm",
                "border",
                "border-gray",
                "shadow-lg",
                "bg-default",
                "overflow-hidden",
                "flex",
                "flex-col",
                "grow",
                hideWhenEmpty && "data-empty:hidden",
              )}
            >
              {listContent}
            </BaseAutocomplete.Popup>
          </BaseAutocomplete.Positioner>
        </BaseAutocomplete.Portal>
      )}
    </BaseAutocomplete.Root>
  );
}
